import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Visibility,
  CheckCircle,
  Cancel,
  Refresh,
  Add,
  TrendingUp,
  AttachMoney,
  Person,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { showToast } from "../../../../../utils/toast";
import {
  getTransactionsAPI,
  getTransactionByIdAPI,
  createTransactionAPI,
  markTransactionCompletedAPI,
  markTransactionFailedAPI,
  refundTransactionAPI,
  getTransactionStatsAPI,
} from "../../../../../api/transactionApi";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(3),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    color: "#fff",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: "#FE5A97",
  },
  filtersCard: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  tableContainer: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    marginBottom: theme.spacing(2),
  },
  table: {
    "& .MuiTableCell-root": {
      borderBottom: "1px solid #333",
      color: "#fff",
    },
    "& .MuiTableHead-root": {
      backgroundColor: "#2a2a2a",
    },
  },
  statusChip: {
    fontSize: "0.75rem",
    fontWeight: "bold",
  },
  statusPending: {
    backgroundColor: "#ff9800",
    color: "#fff",
  },
  statusCompleted: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  statusFailed: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
  statusRefunded: {
    backgroundColor: "#9c27b0",
    color: "#fff",
  },
  actionButton: {
    margin: theme.spacing(0.5),
    minWidth: "auto",
    padding: theme.spacing(0.5, 1),
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  createDialog: {
    "& .MuiDialog-paper": {
      backgroundColor: "#1a1a1a",
      color: "#fff",
      border: "1px solid #333",
    },
  },
}));

interface Transaction {
  _id: string;
  userId: {
    _id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    profileImg?: string;
  };
  type: string;
  status: "pending" | "completed" | "failed" | "refunded";
  amount: number;
  currency: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  credits?: number;
  packageName?: string;
  subscriptionTier?: string;
  paymentMethod?: string;
  paymentDetails?: {
    cardLast4?: string;
    cardBrand?: string;
  };
}

interface TransactionStats {
  summary: {
    totalTransactions: number;
    totalRevenue: number;
    pendingTransactions: number;
    completedTransactions: number;
    failedTransactions: number;
    refundedTransactions: number;
    averageTransactionValue: number;
    completionRate: string;
  };
  breakdown: {
    byType: Array<{ _id: string; count: number }>;
    byStatus: Array<{ _id: string; count: number }>;
  };
  period: string;
  dateRange: {
    start: string;
    end: string;
  };
}

const AdminTransactions: React.FC = () => {
  const classes = useStyles();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<TransactionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    startDate: "",
    endDate: "",
    userId: "",
  });
  const [newTransaction, setNewTransaction] = useState({
    userId: "",
    type: "credit_purchase",
    amount: 0,
    currency: "USD",
    description: "",
    credits: 0,
    packageName: "",
    subscriptionTier: "",
    paymentMethod: "credit_card",
  });

  useEffect(() => {
    loadData();
  }, [filters, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const [transactionsResponse, statsResponse] = await Promise.all([
        getTransactionsAPI({
          ...filters,
          limit: 20,
          offset: (currentPage - 1) * 20,
        }),
        getTransactionStatsAPI(),
      ]);

      if (transactionsResponse.success) {
        setTransactions(transactionsResponse.data);
        setTotalCount(transactionsResponse.totalCount);
        setTotalPages(Math.ceil(transactionsResponse.totalCount / 20));
      }
      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error("Error loading transaction data:", error);
      showToast("Error loading transaction data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (transactionId: string) => {
    try {
      const response = await getTransactionByIdAPI(transactionId);
      if (response.success) {
        setSelectedTransaction(response.data);
        setDetailDialog(true);
      }
    } catch (error) {
      console.error("Error loading transaction details:", error);
      showToast("Error loading transaction details", "error");
    }
  };

  const handleMarkCompleted = async (transactionId: string) => {
    try {
      const response = await markTransactionCompletedAPI(transactionId);
      if (response.success) {
        showToast("Transaction marked as completed", "success");
        loadData();
      }
    } catch (error) {
      console.error("Error marking transaction completed:", error);
      showToast("Error marking transaction completed", "error");
    }
  };

  const handleMarkFailed = async (transactionId: string) => {
    try {
      const response = await markTransactionFailedAPI(transactionId);
      if (response.success) {
        showToast("Transaction marked as failed", "success");
        loadData();
      }
    } catch (error) {
      console.error("Error marking transaction failed:", error);
      showToast("Error marking transaction failed", "error");
    }
  };

  const handleRefund = async (transactionId: string) => {
    try {
      const response = await refundTransactionAPI(transactionId);
      if (response.success) {
        showToast("Transaction refunded successfully", "success");
        loadData();
      }
    } catch (error) {
      console.error("Error refunding transaction:", error);
      showToast("Error refunding transaction", "error");
    }
  };

  const handleCreateTransaction = async () => {
    try {
      const response = await createTransactionAPI(newTransaction);
      if (response.success) {
        showToast("Transaction created successfully", "success");
        setCreateDialog(false);
        setNewTransaction({
          userId: "",
          type: "credit_purchase",
          amount: 0,
          currency: "USD",
          description: "",
          credits: 0,
          packageName: "",
          subscriptionTier: "",
          paymentMethod: "credit_card",
        });
        loadData();
      }
    } catch (error) {
      console.error("Error creating transaction:", error);
      showToast("Error creating transaction", "error");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return classes.statusPending;
      case "completed":
        return classes.statusCompleted;
      case "failed":
        return classes.statusFailed;
      case "refunded":
        return classes.statusRefunded;
      default:
        return classes.statusPending;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4" style={{ color: "#FE5A97" }}>
          Transaction Management
        </Typography>
        <Box display="flex" style={{ gap: "16px" }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          >
            Create Transaction
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadData}
            style={{ backgroundColor: "#FE5A97", color: "#fff" }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics */}
      {stats && (
        <Grid container spacing={2} className={classes.statsGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                  <TrendingUp style={{ color: "#FE5A97" }} />
                  <Typography variant="h6">Total Transactions</Typography>
                </Box>
                <Typography className={classes.statValue}>
                  {stats.summary.totalTransactions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                  <AttachMoney style={{ color: "#FE5A97" }} />
                  <Typography variant="h6">Total Revenue</Typography>
                </Box>
                <Typography className={classes.statValue}>
                  {formatCurrency(stats.summary.totalRevenue, "USD")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
                  <Person style={{ color: "#FE5A97" }} />
                  <Typography variant="h6">Pending</Typography>
                </Box>
                <Typography className={classes.statValue}>
                  {stats.summary.pendingTransactions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6">Completed</Typography>
                <Typography className={classes.statValue}>
                  {stats.summary.completedTransactions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6">Avg Value</Typography>
                <Typography className={classes.statValue}>
                  {formatCurrency(stats.summary.averageTransactionValue, "USD")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6">Completion Rate</Typography>
                <Typography className={classes.statValue}>
                  {stats.summary.completionRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography variant="h6">Failed</Typography>
                <Typography className={classes.statValue}>
                  {stats.summary.failedTransactions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Breakdown Section */}
      {stats && stats.breakdown && (
        <Grid container spacing={2} style={{ marginBottom: "24px" }}>
          <Grid item xs={12} md={6}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", marginBottom: "16px" }}
                >
                  Transactions by Type
                </Typography>
                {stats.breakdown.byType.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    style={{ marginBottom: "8px" }}
                  >
                    <Typography
                      style={{ color: "#fff", textTransform: "capitalize" }}
                    >
                      {item._id.replace("_", " ")}
                    </Typography>
                    <Typography
                      style={{ color: "#FE5A97", fontWeight: "bold" }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card className={classes.statCard}>
              <CardContent>
                <Typography
                  variant="h6"
                  style={{ color: "#fff", marginBottom: "16px" }}
                >
                  Transactions by Status
                </Typography>
                {stats.breakdown.byStatus.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    style={{ marginBottom: "8px" }}
                  >
                    <Typography
                      style={{ color: "#fff", textTransform: "capitalize" }}
                    >
                      {item._id}
                    </Typography>
                    <Typography
                      style={{ color: "#FE5A97", fontWeight: "bold" }}
                    >
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card className={classes.filtersCard}>
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{ gap: "16px" }}
        >
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 120 }}
          >
            <InputLabel style={{ color: "#fff" }}>Type</InputLabel>
            <Select
              value={filters.type}
              onChange={e =>
                setFilters({ ...filters, type: e.target.value as string })
              }
              label="Type"
              style={{ color: "#fff" }}
            >
              <MenuItem value="" style={{ color: "#fff" }}>
                All Types
              </MenuItem>
              <MenuItem value="credit_purchase" style={{ color: "#fff" }}>
                Credit Purchase
              </MenuItem>
              <MenuItem value="subscription" style={{ color: "#fff" }}>
                Subscription
              </MenuItem>
              <MenuItem value="tip" style={{ color: "#fff" }}>
                Tip
              </MenuItem>
              <MenuItem value="refund" style={{ color: "#fff" }}>
                Refund
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 120 }}
          >
            <InputLabel style={{ color: "#fff" }}>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={e =>
                setFilters({ ...filters, status: e.target.value as string })
              }
              label="Status"
              style={{ color: "#fff" }}
            >
              <MenuItem value="" style={{ color: "#fff" }}>
                All Status
              </MenuItem>
              <MenuItem value="pending" style={{ color: "#fff" }}>
                Pending
              </MenuItem>
              <MenuItem value="completed" style={{ color: "#fff" }}>
                Completed
              </MenuItem>
              <MenuItem value="failed" style={{ color: "#fff" }}>
                Failed
              </MenuItem>
              <MenuItem value="refunded" style={{ color: "#fff" }}>
                Refunded
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="date"
            // label="Start Date"
            size="small"
            value={filters.startDate}
            onChange={e =>
              setFilters({ ...filters, startDate: e.target.value })
            }
            InputLabelProps={{ style: { color: "#fff" } }}
            InputProps={{ style: { color: "#fff" } }}
            style={{ minWidth: 150 }}
          />

          <TextField
            type="date"
            // label="End Date"
            size="small"
            value={filters.endDate}
            onChange={e => setFilters({ ...filters, endDate: e.target.value })}
            InputLabelProps={{ style: { color: "#fff" } }}
            InputProps={{ style: { color: "#fff" } }}
            style={{ minWidth: 150 }}
          />

          <TextField
            label="User ID"
            size="small"
            value={filters.userId}
            onChange={e => setFilters({ ...filters, userId: e.target.value })}
            InputLabelProps={{ style: { color: "#fff" } }}
            InputProps={{ style: { color: "#fff" } }}
            style={{ minWidth: 150 }}
          />
        </Box>
      </Card>

      {/* Transactions Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map(transaction => (
              <TableRow key={transaction._id}>
                <TableCell>
                  <Box
                    display="flex"
                    alignItems="center"
                    style={{ gap: "8px" }}
                  >
                    <Avatar src={transaction.userId?.profileImg} />
                    <Box>
                      <Typography variant="body2" style={{ color: "#fff" }}>
                        {transaction.userId?.username || "Unknown User"}
                      </Typography>
                      <Typography variant="caption" style={{ color: "#ccc" }}>
                        {transaction.userId?.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell style={{ color: "#fff" }}>
                  {transaction.type.replace("_", " ").toUpperCase()}
                </TableCell>
                <TableCell style={{ color: "#fff" }}>
                  {formatCurrency(transaction.amount, transaction.currency)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={transaction.status.toUpperCase()}
                    className={`${classes.statusChip} ${getStatusColor(
                      transaction.status
                    )}`}
                    size="small"
                  />
                </TableCell>
                <TableCell style={{ color: "#fff" }}>
                  {formatDate(transaction.createdAt)}
                </TableCell>
                <TableCell>
                  <Box display="flex" style={{ gap: "4px" }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(transaction._id)}
                        style={{ color: "#2196f3" }}
                      >
                        <Visibility style={{ fontSize: "24px" }} />
                      </IconButton>
                    </Tooltip>
                    {transaction.status === "pending" && (
                      <>
                        <Tooltip title="Mark Completed">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkCompleted(transaction._id)}
                            style={{ color: "#4caf50" }}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark Failed">
                          <IconButton
                            size="small"
                            onClick={() => handleMarkFailed(transaction._id)}
                            style={{ color: "#f44336" }}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {transaction.status === "completed" && (
                      <Tooltip title="Refund">
                        <IconButton
                          size="small"
                          onClick={() => handleRefund(transaction._id)}
                          style={{ color: "#9c27b0" }}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box className={classes.pagination}>
        <Button
          variant="outlined"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={{ color: "#fff", borderColor: "#333" }}
        >
          Previous
        </Button>
        <Typography style={{ color: "#fff" }}>
          Page {currentPage} of {totalPages} ({totalCount} total)
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{ color: "#fff", borderColor: "#333" }}
        >
          Next
        </Button>
      </Box>

      {/* Transaction Details Dialog */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
        className={classes.createDialog}
      >
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" style={{ color: "#fff" }}>
                  Transaction Information
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>ID:</strong> {selectedTransaction._id}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Type:</strong> {selectedTransaction.type}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Amount:</strong>{" "}
                  {formatCurrency(
                    selectedTransaction.amount,
                    selectedTransaction.currency
                  )}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Status:</strong> {selectedTransaction.status}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Description:</strong>{" "}
                  {selectedTransaction.description}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" style={{ color: "#fff" }}>
                  User Information
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Username:</strong>{" "}
                  {selectedTransaction.userId?.username}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Email:</strong> {selectedTransaction.userId?.email}
                </Typography>
                <Typography style={{ color: "#fff" }}>
                  <strong>Name:</strong> {selectedTransaction.userId?.firstName}{" "}
                  {selectedTransaction.userId?.lastName}
                </Typography>
              </Grid>
              {selectedTransaction.credits && (
                <Grid item xs={12}>
                  <Typography variant="h6" style={{ color: "#fff" }}>
                    Package Details
                  </Typography>
                  <Typography style={{ color: "#fff" }}>
                    <strong>Credits:</strong> {selectedTransaction.credits}
                  </Typography>
                  {selectedTransaction.packageName && (
                    <Typography style={{ color: "#fff" }}>
                      <strong>Package:</strong>{" "}
                      {selectedTransaction.packageName}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Transaction Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="sm"
        fullWidth
        className={classes.createDialog}
      >
        <DialogTitle>Create New Transaction</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="User ID"
                value={newTransaction.userId}
                onChange={e =>
                  setNewTransaction({
                    ...newTransaction,
                    userId: e.target.value,
                  })
                }
                InputLabelProps={{ style: { color: "#fff" } }}
                InputProps={{ style: { color: "#fff" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel style={{ color: "#fff" }}>Type</InputLabel>
                <Select
                  value={newTransaction.type}
                  onChange={e =>
                    setNewTransaction({
                      ...newTransaction,
                      type: e.target.value as string,
                    })
                  }
                  label="Type"
                  style={{ color: "#fff" }}
                >
                  <MenuItem value="credit_purchase">Credit Purchase</MenuItem>
                  <MenuItem value="subscription">Subscription</MenuItem>
                  <MenuItem value="tip">Tip</MenuItem>
                  <MenuItem value="refund">Refund</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={newTransaction.amount}
                onChange={e =>
                  setNewTransaction({
                    ...newTransaction,
                    amount: parseFloat(e.target.value),
                  })
                }
                InputLabelProps={{ style: { color: "#fff" } }}
                InputProps={{ style: { color: "#fff" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newTransaction.description}
                onChange={e =>
                  setNewTransaction({
                    ...newTransaction,
                    description: e.target.value,
                  })
                }
                InputLabelProps={{ style: { color: "#fff" } }}
                InputProps={{ style: { color: "#fff" } }}
              />
            </Grid>
            {newTransaction.type === "credit_purchase" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Credits"
                  type="number"
                  value={newTransaction.credits}
                  onChange={e =>
                    setNewTransaction({
                      ...newTransaction,
                      credits: parseInt(e.target.value),
                    })
                  }
                  InputLabelProps={{ style: { color: "#fff" } }}
                  InputProps={{ style: { color: "#fff" } }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTransaction}
            variant="contained"
            style={{ backgroundColor: "#FE5A97", color: "#fff" }}
          >
            Create Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTransactions;
